import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Calendar, Check, ChevronDown, ChevronRight, Copy, ExternalLink, Globe,
  Loader2, Monitor, Smartphone, UtensilsCrossed,
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import AccessDenied from '../components/AccessDenied';
import { confirm } from '../components/ui/confirm-dialog';
import { useRestaurant } from '../context/RestaurantContext';
import { useModuleAccess } from '../utils/useModuleAccess';
import apiClient from '../services/api';
import { locationsService } from '../services/locationsService';
import { useT } from '../context/LanguageContext';
import { getAllMenus, getMenu, updateMenuWebsite } from '../services/menuService';
import WebsitePhotos from './WebsitePhotos';
import FinderPlacesManagement from './FinderPlacesManagement';

/** Website Settings: traffic, photos, which branch the site speaks for and the places its table finder offers, all in one place. */

// The one chart hue, validated against the light surface; these are single-series magnitudes, so color carries nothing.
const BAR = '#4f46e5';

// The six sections, each its own sidebar entry and address; navigation lives in the sidebar alone.
const SECTIONS = ['traffic', 'photos', 'menus', 'reservations', 'site', 'places'];

const fmtDay = (iso) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString([], { day: 'numeric', month: 'short' });

const fmtHour = (h) =>
  new Date(2000, 0, 1, h).toLocaleTimeString([], { hour: 'numeric' });

/* ── Traffic ─────────────────────────────────────────────────────────── */

function StatTile({ label, value, sub }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="text-sm text-muted-foreground">{label}</div>
        <div className="mt-1 text-2xl font-bold text-foreground">{value}</div>
        {sub && <div className="mt-0.5 text-xs text-muted-foreground">{sub}</div>}
      </CardContent>
    </Card>
  );
}

// Horizontal magnitude bars with values in text ink beside them, never on the color.
function HBarList({ rows, unit }) {
  const max = Math.max(...rows.map((r) => r.value), 1);
  return (
    <div className="space-y-2">
      {rows.map((r) => (
        <div key={r.label} className="flex items-center gap-3" title={`${r.label} — ${r.value} ${unit}`}>
          <span className="w-28 shrink-0 truncate text-xs text-muted-foreground">{r.label}</span>
          <div className="h-3 flex-1 rounded-r bg-muted">
            <div
              className="h-3 rounded-r"
              style={{ width: `${(r.value / max) * 100}%`, background: BAR, minWidth: r.value ? 4 : 0 }}
            />
          </div>
          <span className="w-10 shrink-0 text-right text-xs font-medium text-foreground">{r.value}</span>
        </div>
      ))}
    </div>
  );
}

function TrafficPanel({ restaurantId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!restaurantId) return;
    let cancelled = false;
    setLoading(true);
    apiClient.get(`/users/restaurant/${restaurantId}/website/insights/`)
      .then(({ data: res }) => {
        if (cancelled) return;
        if (!res?.success) throw new Error(res?.error || 'Failed to load insights');
        setData(res.data);
        setError('');
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.response?.data?.error || e.message || 'Failed to load insights');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [restaurantId]);

  const peak = useMemo(() => {
    if (!data) return null;
    return data.series.reduce((a, b) => (b.views > (a?.views || 0) ? b : a), null);
  }, [data]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-8 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading traffic…
      </div>
    );
  }
  if (error) {
    return (
      <div className="rounded-lg border border-red-300 bg-red-50 p-4 text-sm text-red-800
                      dark:border-red-800 dark:bg-red-950/40 dark:text-red-200">
        {error}
      </div>
    );
  }
  if (!data) return null;

  const { totals, series, top_pages: topPages, devices, hours, branches, referrers, bookings } = data;
  const noTraffic = totals.month.views === 0;
  const maxDay = Math.max(...series.map((d) => d.views), 1);
  const maxHour = Math.max(...hours, 1);
  const peakHour = hours.indexOf(Math.max(...hours));
  const deviceTotal = devices.mobile + devices.desktop;

  return (
    <div className="space-y-6">
      {/* Headline numbers */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatTile label="Views today" value={totals.today.views}
          sub={`${totals.today.visitors} visitor${totals.today.visitors === 1 ? '' : 's'}`} />
        <StatTile label="Last 7 days" value={totals.week.views}
          sub={`${totals.week.visitors} visitor${totals.week.visitors === 1 ? '' : 's'}`} />
        <StatTile label={`Last ${data.window_days} days`} value={totals.month.views}
          sub={`${totals.month.visitors} visitor${totals.month.visitors === 1 ? '' : 's'}`} />
        <StatTile label="Bookings from the website" value={bookings.month}
          sub={`${bookings.week} this week · ${bookings.today} today`} />
      </div>

      {noTraffic && (
        <div className="rounded-lg border border-border bg-card p-4 text-sm text-muted-foreground">
          No visits recorded yet. The website reports each view here by itself —
          numbers appear as soon as someone opens your public site.
        </div>
      )}

      {/* Views per day — 30 columns, hover for the exact numbers */}
      <Card>
        <CardContent className="p-4">
          <div className="mb-3 flex items-baseline justify-between">
            <h3 className="text-sm font-semibold text-foreground">Views per day</h3>
            {peak && peak.views > 0 && (
              <span className="text-xs text-muted-foreground">
                busiest: {fmtDay(peak.date)} · {peak.views} views
              </span>
            )}
          </div>
          <div className="flex h-32 items-end gap-[3px]">
            {series.map((d) => (
              <div
                key={d.date}
                className="group relative flex-1"
                title={`${fmtDay(d.date)} — ${d.views} view${d.views === 1 ? '' : 's'}, ${d.visitors} visitor${d.visitors === 1 ? '' : 's'}`}
              >
                <div
                  className="w-full rounded-t"
                  style={{
                    height: `${(d.views / maxDay) * 120}px`,
                    background: d.views ? BAR : 'transparent',
                  }}
                />
              </div>
            ))}
          </div>
          <div className="mt-1 flex justify-between text-[11px] text-muted-foreground">
            <span>{fmtDay(series[0].date)}</span>
            <span>{fmtDay(series[Math.floor(series.length / 2)].date)}</span>
            <span>{fmtDay(series[series.length - 1].date)}</span>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-3">
        {/* Top pages */}
        <Card>
          <CardContent className="p-4">
            <h3 className="mb-3 text-sm font-semibold text-foreground">Most viewed</h3>
            {topPages.length === 0 ? (
              <p className="text-sm text-muted-foreground">Nothing yet.</p>
            ) : (
              <HBarList unit="views"
                rows={topPages.map((p) => ({ label: p.path, value: p.views }))} />
            )}
          </CardContent>
        </Card>

        {/* Devices */}
        <Card>
          <CardContent className="p-4">
            <h3 className="mb-3 text-sm font-semibold text-foreground">Devices</h3>
            {deviceTotal === 0 ? (
              <p className="text-sm text-muted-foreground">Nothing yet.</p>
            ) : (
              <div className="space-y-3">
                <div className="flex h-3 overflow-hidden rounded bg-muted"
                  title={`${devices.mobile} mobile · ${devices.desktop} desktop`}>
                  <div style={{ width: `${(devices.mobile / deviceTotal) * 100}%`, background: BAR }} />
                </div>
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span className="inline-flex items-center gap-1.5">
                    <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: BAR }} />
                    <Smartphone className="h-3.5 w-3.5" /> Mobile
                    <strong className="text-foreground">
                      {Math.round((devices.mobile / deviceTotal) * 100)}%
                    </strong>
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <span className="inline-block h-2.5 w-2.5 rounded-sm bg-muted" />
                    <Monitor className="h-3.5 w-3.5" /> Desktop
                    <strong className="text-foreground">
                      {Math.round((devices.desktop / deviceTotal) * 100)}%
                    </strong>
                  </span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Busiest hours */}
        <Card>
          <CardContent className="p-4">
            <div className="mb-3 flex items-baseline justify-between">
              <h3 className="text-sm font-semibold text-foreground">Busiest hours</h3>
              {maxHour > 0 && hours[peakHour] > 0 && (
                <span className="text-xs text-muted-foreground">peak {fmtHour(peakHour)}</span>
              )}
            </div>
            <div className="flex h-16 items-end gap-[2px]">
              {hours.map((n, h) => (
                <div key={h} className="flex-1"
                  title={`${fmtHour(h)} — ${n} view${n === 1 ? '' : 's'}`}>
                  <div className="w-full rounded-t"
                    style={{ height: `${(n / maxHour) * 56}px`, background: n ? BAR : 'transparent' }} />
                </div>
              ))}
            </div>
            <div className="mt-1 flex justify-between text-[11px] text-muted-foreground">
              <span>12 am</span><span>noon</span><span>11 pm</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {(branches.length > 1 || referrers.length > 0) && (
        <div className="grid gap-4 lg:grid-cols-2">
          {branches.length > 1 && (
            <Card>
              <CardContent className="p-4">
                <h3 className="mb-3 text-sm font-semibold text-foreground">Views per branch</h3>
                <HBarList unit="views"
                  rows={branches.map((b) => ({ label: b.name, value: b.views }))} />
              </CardContent>
            </Card>
          )}
          {referrers.length > 0 && (
            <Card>
              <CardContent className="p-4">
                <h3 className="mb-3 text-sm font-semibold text-foreground">Visitors came from</h3>
                <HBarList unit="views"
                  rows={referrers.map((r) => ({ label: r.referrer, value: r.views }))} />
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Menus ───────────────────────────────────────────────────────────── */

/* Which menus the site prints and how much of each: marketing decisions about the SITE, not edits to the menu. */
function MenusPanel({ restaurantId, canEdit }) {
  const [menus, setMenus] = useState(null);
  const [error, setError] = useState('');
  // Per-menu section list fetched when a menu opens and cached, so re-opening costs nothing.
  const [details, setDetails] = useState({});
  const [openId, setOpenId] = useState(null);
  const [savingId, setSavingId] = useState(null);

  useEffect(() => {
    if (!restaurantId) return;
    let cancelled = false;
    getAllMenus(restaurantId)
      .then((rows) => { if (!cancelled) setMenus(rows || []); })
      .catch((e) => { if (!cancelled) { setMenus([]); setError(e?.error || e?.message || 'Failed to load menus'); } });
    return () => { cancelled = true; };
  }, [restaurantId]);

  // Which sections' dish lists are unfolded, as "menuId:sectionId" keys.
  const [openSecs, setOpenSecs] = useState(() => new Set());
  const toggleSec = (menuId, sectionId) => {
    const k = `${menuId}:${sectionId}`;
    setOpenSecs((p) => {
      const next = new Set(p);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  };

  const loadDetail = async (menuId) => {
    if (details[menuId]) return;
    try {
      const data = await getMenu(restaurantId, menuId);
      const sections = (data.sections || []).map((s) => {
        // One row per dish, name and id - the id is what website_excluded_dishes carries.
        const seen = new Set();
        const items = (s.items || []).filter((it) => {
          const id = Number(it.dish_id);
          if (!id || seen.has(id)) return false;
          seen.add(id);
          return true;
        }).map((it) => ({ id: Number(it.dish_id), name: it.dish_name || 'Dish' }));
        return { id: Number(s.id), name: s.name, dishes: items.length, items };
      });
      setDetails((p) => ({ ...p, [menuId]: { sections } }));
    } catch (e) {
      setDetails((p) => ({ ...p, [menuId]: { sections: [], error: e?.error || e?.message || 'Failed to load sections' } }));
    }
  };

  const toggleOpen = (m) => {
    const next = openId === m.id ? null : m.id;
    setOpenId(next);
    if (next) loadDetail(m.id);
  };

  /* Switching a menu ON now ticks the categories it has today, since publishing an empty card printed nothing. */
  const publishOn = async (menu) => {
    if ((menu.website_sections || []).length > 0) {
      save(menu, { show_on_website: true });
      loadDetail(menu.id);
      return;
    }
    setSavingId(menu.id);
    setError('');
    const previous = menus;
    try {
      const data = await getMenu(restaurantId, menu.id);
      const ids = (data.sections || []).map((s) => Number(s.id)).filter(Boolean);
      const patch = { show_on_website: true, website_sections: ids };
      setMenus((p) => p.map((m) => (m.id === menu.id ? { ...m, ...patch } : m)));
      await updateMenuWebsite(restaurantId, menu.id, patch);
      loadDetail(menu.id);
    } catch (e) {
      setMenus(previous);
      setError(e?.error || e?.message || 'Could not put that menu on the website');
    } finally {
      setSavingId(null);
    }
  };

  /* Optimistic write-through: flip the row, save, and put it back on failure. */
  const save = async (menu, patch) => {
    const previous = menus;
    setMenus((p) => p.map((m) => (m.id === menu.id ? { ...m, ...patch } : m)));
    setSavingId(menu.id);
    setError('');
    try {
      await updateMenuWebsite(restaurantId, menu.id, patch);
    } catch (e) {
      setMenus(previous);
      setError(e?.error || e?.message || 'Could not save that change');
    } finally {
      setSavingId(null);
    }
  };

  if (menus === null) {
    return (
      <div className="flex items-center gap-2 py-8 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading menus…
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {error && (
        <div className="rounded-lg border border-red-300 bg-red-50 p-4 text-sm text-red-800
                        dark:border-red-800 dark:bg-red-950/40 dark:text-red-200">
          {error}
        </div>
      )}

      <p className="text-sm text-muted-foreground">
        Which menus your website prints. Built under{' '}
        <span className="font-medium text-foreground">Menu &amp; Dishes</span>.
      </p>

      {menus.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <UtensilsCrossed className="h-10 w-10 mx-auto mb-3 opacity-30" />
            No menus yet — create one under Menu &amp; Dishes first.
          </CardContent>
        </Card>
      ) : menus.map((m) => {
        const open = openId === m.id;
        const det = details[m.id];
        const ticked = new Set((m.website_sections || []).map(Number));
        const sections = det?.sections || [];
        return (
          <Card key={m.id} className={m.show_on_website ? '' : 'opacity-80'}>
            <CardContent className="p-4">
              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => toggleOpen(m)}
                  className="inline-flex items-center gap-2 min-w-0 text-left"
                  title={open ? 'Collapse' : 'Choose what this menu shows'}
                >
                  {open ? <ChevronDown size={16} className="shrink-0 text-muted-foreground" />
                    : <ChevronRight size={16} className="shrink-0 text-muted-foreground" />}
                  <span className="font-semibold text-foreground truncate">{m.name}</span>
                </button>
                <span className="px-2 py-0.5 text-[11px] rounded-full font-medium bg-secondary text-secondary-foreground">
                  {m.menu_type === 'BUFFET' ? 'Buffet' : 'À la carte'}
                </span>
                {!m.is_active && (
                  <span className="px-2 py-0.5 text-[11px] rounded-full font-medium bg-red-50 text-red-600">
                    Inactive — never shown
                  </span>
                )}
                {m.show_on_website && m.website_hours_only && (
                  <span className="px-2 py-0.5 text-[11px] rounded-full font-medium bg-sky-100 text-sky-800"
                    title="Only the menu's name and serving hours print on the website — no dishes, no prices">
                    Timings only
                  </span>
                )}
                {m.show_on_website && !m.website_hours_only && (m.website_sections || []).length === 0 && (
                  <span className="px-2 py-0.5 text-[11px] rounded-full font-medium bg-amber-100 text-amber-800"
                    title="The menu is switched on but no categories are ticked, so no dishes reach the website">
                    Nothing ticked
                  </span>
                )}
                {savingId === m.id && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
                <label className="ml-auto flex items-center gap-2 cursor-pointer select-none shrink-0">
                  <span className="text-sm text-muted-foreground">Show on website</span>
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    disabled={!canEdit || savingId === m.id}
                    checked={!!m.show_on_website}
                    onChange={(e) => {
                      if (!e.target.checked) { save(m, { show_on_website: false }); return; }
                      // Turning it ON also ticks its categories when it has none, or the site publishes an empty card.
                      setOpenId(m.id);
                      publishOn(m);
                    }}
                  />
                </label>
              </div>

              {open && (
                <div className="mt-3 ml-6 rounded-md border border-border px-3 py-2.5 space-y-2.5">
                  {!det ? (
                    <p className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading categories…
                    </p>
                  ) : det.error ? (
                    <p className="text-xs text-red-600">{det.error}</p>
                  ) : (
                    <>
                      {/* What the website prints of this menu: the card in full, or just its name and serving hours. */}
                      <div className="flex flex-wrap gap-2">
                        {[
                          [false, 'Categories & dishes', 'The ticked categories below, dish by dish'],
                          [true, 'Timings only', 'Just the menu’s name and serving hours — no dishes, no prices'],
                        ].map(([val, label, hint]) => (
                          <button
                            key={String(val)}
                            type="button"
                            disabled={!canEdit || savingId === m.id}
                            title={hint}
                            onClick={() => save(m, { website_hours_only: val })}
                            className={`rounded-md border px-3 py-1.5 text-xs font-medium transition ${
                              !!m.website_hours_only === val
                                ? 'border-primary bg-primary/5 text-foreground ring-1 ring-primary/30'
                                : 'border-border text-muted-foreground hover:text-foreground'
                            }`}
                          >
                            {label}
                          </button>
                        ))}
                      </div>

                      {m.website_hours_only ? (
                        <p className="rounded border border-sky-200 bg-sky-50 px-2 py-1.5 text-xs text-sky-800
                                      dark:border-sky-800 dark:bg-sky-950/40 dark:text-sky-200">
                          The website prints only “{m.name}” and its serving hours. Dishes and
                          prices stay off the page — nothing below applies while this is on.
                        </p>
                      ) : (
                      <>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs uppercase tracking-wider font-mono font-semibold text-foreground">
                          Categories on the website
                        </span>
                        {sections.length > 0 && (
                          <span className="text-xs text-muted-foreground shrink-0">
                            {sections.filter((s) => ticked.has(s.id)).length} of {sections.length}
                          </span>
                        )}
                      </div>
                      {sections.length === 0 ? (
                        <p className="text-xs text-muted-foreground">
                          This menu has no sections yet — add them in the menu builder.
                        </p>
                      ) : (
                        <>
                          <div className="space-y-1 max-h-64 overflow-y-auto">
                            {sections.map((s) => {
                              const hidden = new Set((m.website_excluded_dishes || []).map(Number));
                              const shown = s.items.filter((it) => !hidden.has(it.id)).length;
                              const secOpen = openSecs.has(`${m.id}:${s.id}`);
                              return (
                                <div key={s.id}>
                                  <div className="flex items-center gap-2 rounded px-1.5 py-1 text-sm hover:bg-secondary/40">
                                    <input
                                      type="checkbox"
                                      className="h-4 w-4 shrink-0"
                                      disabled={!canEdit || savingId === m.id}
                                      checked={ticked.has(s.id)}
                                      onChange={() => {
                                        const next = new Set(ticked);
                                        if (next.has(s.id)) next.delete(s.id); else next.add(s.id);
                                        save(m, { website_sections: [...next] });
                                      }}
                                    />
                                    <span className="min-w-0 truncate">{s.name}</span>
                                    {/* The way into the per-dish ticks: only a ticked category has dishes to curate. */}
                                    {ticked.has(s.id) && s.items.length > 0 ? (
                                      <button
                                        type="button"
                                        onClick={() => toggleSec(m.id, s.id)}
                                        className="ml-auto shrink-0 inline-flex items-center gap-1 text-xs text-primary hover:underline"
                                        title="Choose which dishes in this category the website prints"
                                      >
                                        {secOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                                        {shown} of {s.items.length} {s.items.length === 1 ? 'dish' : 'dishes'}
                                      </button>
                                    ) : (
                                      <span className="ml-auto shrink-0 text-xs text-muted-foreground">
                                        {s.dishes} {s.dishes === 1 ? 'dish' : 'dishes'}
                                      </span>
                                    )}
                                  </div>
                                  {ticked.has(s.id) && secOpen && (
                                    <div className="ml-7 mb-1 space-y-0.5">
                                      {s.items.map((it) => (
                                        <label key={it.id}
                                          className="flex items-center gap-2 rounded px-1.5 py-0.5 text-xs cursor-pointer hover:bg-secondary/40">
                                          <input
                                            type="checkbox"
                                            className="h-3.5 w-3.5 shrink-0"
                                            disabled={!canEdit || savingId === m.id}
                                            checked={!hidden.has(it.id)}
                                            onChange={() => {
                                              const next = new Set(hidden);
                                              if (next.has(it.id)) next.delete(it.id); else next.add(it.id);
                                              save(m, { website_excluded_dishes: [...next] });
                                            }}
                                          />
                                          <span className="min-w-0 truncate">{it.name}</span>
                                        </label>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                          <div className="flex items-center gap-3 text-xs">
                            <button type="button" className="text-primary hover:underline"
                              disabled={!canEdit}
                              onClick={() => save(m, { website_sections: sections.map((s) => s.id) })}>
                              Select all
                            </button>
                            <button type="button" className="text-muted-foreground hover:underline"
                              disabled={!canEdit}
                              onClick={() => save(m, { website_sections: [] })}>
                              Clear
                            </button>
                          </div>
                          {ticked.size === 0 && (
                            <p className="rounded border border-amber-200 bg-amber-50 px-2 py-1.5 text-xs text-amber-800
                                          dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
                              Nothing ticked — no dishes from this menu will reach the website.
                            </p>
                          )}
                        </>
                      )}
                      <label className="flex items-start gap-2.5 pt-1 border-t border-border cursor-pointer">
                        <input
                          type="checkbox"
                          className="h-4 w-4 mt-0.5"
                          disabled={!canEdit || savingId === m.id}
                          checked={m.website_show_prices !== false}
                          onChange={(e) => save(m, { website_show_prices: e.target.checked })}
                        />
                        <span className="min-w-0">
                          <span className="block text-sm font-medium text-foreground">
                            Show prices on the website
                          </span>
                          <span className="block text-xs text-muted-foreground mt-0.5">
                            {m.website_show_prices !== false
                              ? 'Each dish prints its price, and each category its price range.'
                              : 'Dish names only. Prices still show on the QR menu, the order screen and bills.'}
                          </span>
                        </span>
                      </label>
                      </>
                      )}
                    </>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

/* ── Reservations ────────────────────────────────────────────────────── */

/* The SAME switch as "Accept website reservations" in Reservations - one field written straight through. */
function ReservationsPanel({ restaurantId, canEdit }) {
  const { t } = useT();
  /* The switch is the branch's the site speaks for (rules are per branch
     since users/0116). Read here rather than passed down: the Site tab that
     owns the picker is a sibling that may never have been opened. undefined
     = not known yet; null = the site has no branch chosen. */
  const [locationId, setLocationId] = useState(undefined);
  useEffect(() => {
    if (!restaurantId) return undefined;
    let cancelled = false;
    locationsService.listWithSiteBranch(restaurantId)
      .then(({ publicSiteLocationId }) => { if (!cancelled) setLocationId(publicSiteLocationId || null); })
      .catch(() => { if (!cancelled) setLocationId(null); });
    return () => { cancelled = true; };
  }, [restaurantId]);
  const locQ = locationId ? `?location=${locationId}` : '';
  const [enabled, setEnabled] = useState(null);   // null until loaded
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!restaurantId || locationId === undefined) return;
    let cancelled = false;
    apiClient.get(`/users/restaurant/${restaurantId}/reservation-settings/${locQ}`)
      .then(({ data }) => {
        if (cancelled) return;
        if (!data?.success) throw new Error(data?.error || 'Failed to load');
        setEnabled(!!data.data.online_reservations_enabled);
        setError('');
      })
      .catch((e) => {
        if (!cancelled) setError(e?.response?.data?.error || e.message || 'Failed to load');
      });
    return () => { cancelled = true; };
  }, [restaurantId, locationId]);  // eslint-disable-line react-hooks/exhaustive-deps

  const flip = async (next) => {
    const ok = await confirm(next
      ? {
          title: 'Open website reservations?',
          description: 'The public booking page will start accepting new reservations again.',
          confirmText: 'Open reservations',
        }
      : {
          title: 'Close website reservations?',
          description: "The public booking page will show 'reservations closed' and no new "
            + 'website bookings will be accepted. Existing bookings are unaffected.',
          confirmText: 'Close reservations',
          tone: 'destructive',
        });
    if (!ok) return;
    const previous = enabled;
    setEnabled(next);           // optimistic — revert on failure
    setSaving(true);
    setError('');
    try {
      const { data } = await apiClient.put(
        `/users/restaurant/${restaurantId}/reservation-settings/${locQ}`,
        { online_reservations_enabled: next, location: locationId });
      if (!data?.success) throw new Error(data?.error || 'Failed to save');
      setEnabled(!!data.data.online_reservations_enabled);
    } catch (e) {
      setEnabled(previous);
      setError(e?.response?.data?.error || e.message || 'Could not change the switch');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4 max-w-2xl">
      {locationId === null && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800">
          {t('settings.website.branchNeeded')}
        </div>
      )}
      {error && (
        <div className="rounded-lg border border-red-300 bg-red-50 p-4 text-sm text-red-800
                        dark:border-red-800 dark:bg-red-950/40 dark:text-red-200">
          {error}
        </div>
      )}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground">
                <Calendar className="h-4 w-4 text-primary" />
                Reservations on the website
              </h3>
              <p className="mt-1 text-xs text-muted-foreground max-w-md">
                Off, the booking page closes to new bookings. Existing ones are unaffected.
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={!!enabled}
              disabled={!canEdit || saving || enabled === null || !locationId}
              onClick={() => flip(!enabled)}
              className={`relative h-6 w-11 shrink-0 rounded-full transition disabled:opacity-50 ${
                enabled ? 'bg-primary' : 'bg-muted-foreground/25'
              }`}
              title={enabled === null ? 'Loading…'
                : enabled ? 'Website bookings are open — click to close'
                : 'Website bookings are closed — click to open'}
            >
              <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${
                enabled ? 'left-[22px]' : 'left-0.5'
              }`} />
            </button>
          </div>
          <p className="mt-3 rounded-md border border-border bg-secondary/30 px-3 py-2 text-xs text-muted-foreground">
            The same switch as <span className="font-medium text-foreground">Settings →
            Reservations</span>.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

/* ── Site ────────────────────────────────────────────────────────────── */

/* The public pages this admin serves alongside itself on the same tenant
   domain (frontend/scripts/sync_public_site.cjs folds them into the build):
   the marketing website at /site/ and the customer buffet page at
   /justbuffets/. Derived from the current origin, so every tenant gets its
   own without a per-tenant build; REACT_APP_PUBLIC_SITE_URL overrides the
   website when it is hosted elsewhere, exactly as the Locations screen does. */
const publicSites = () => {
  const origin = window.location.origin;
  const site = `${(process.env.REACT_APP_PUBLIC_SITE_URL || `${origin}/site`).replace(/\/+$/, '')}/`;
  return [
    { key: 'site', url: site },
    { key: 'justbuffets', url: `${origin}/justbuffets/` },
  ];
};

function SitePanel({ restaurantId, canEdit }) {
  const { t } = useT();
  const [locations, setLocations] = useState([]);
  // Which address was just copied, so its button can say so for a moment.
  const [copied, setCopied] = useState('');
  const [siteLocationId, setSiteLocationId] = useState(null);
  const [savingSite, setSavingSite] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!restaurantId) return;
    let cancelled = false;
    setLoading(true);
    locationsService.listWithSiteBranch(restaurantId)
      .then(({ locations: locs, publicSiteLocationId }) => {
        if (cancelled) return;
        setLocations(locs);
        setSiteLocationId(publicSiteLocationId);
        setError('');
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.response?.data?.error || e.message || 'Failed to load branches');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [restaurantId]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-8 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading…
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="rounded-lg border border-red-300 bg-red-50 p-4 text-sm text-red-800
                        dark:border-red-800 dark:bg-red-950/40 dark:text-red-200">
          {error}
        </div>
      )}

      {/* The addresses themselves — what goes on a menu card or a QR code.
          Served next to this admin on the same domain, so they are derived,
          not configured. */}
      <Card>
        <CardContent className="p-4">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground">
            <Globe className="h-4 w-4 text-primary" />
            {t('settings.website.urls.title')}
          </h3>
          <p className="mt-1 mb-3 text-xs text-muted-foreground">{t('settings.website.urls.hint')}</p>
          <div className="divide-y divide-border">
            {publicSites().map((s) => (
              <div key={s.key} className="flex flex-wrap items-center justify-between gap-3 py-2.5">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-foreground">
                    {s.key === 'site' ? t('settings.website.urls.site') : t('settings.website.urls.justbuffets')}
                  </div>
                  <a
                    href={s.url}
                    target="_blank"
                    rel="noreferrer"
                    className="block truncate font-mono text-sm text-primary hover:underline"
                  >
                    {s.url}
                  </a>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <a
                    href={s.url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-xs font-medium text-foreground hover:bg-muted"
                  >
                    <ExternalLink className="h-3.5 w-3.5" /> {t('settings.website.urls.open')}
                  </a>
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(s.url);
                        setCopied(s.key);
                        setTimeout(() => setCopied(''), 1600);
                      } catch {
                        /* Clipboard blocked (http, permissions): the address above is selectable. */
                      }
                    }}
                    className="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-xs font-medium text-foreground hover:bg-muted"
                  >
                    {copied === s.key
                      ? <Check className="h-3.5 w-3.5 text-success" />
                      : <Copy className="h-3.5 w-3.5" />}
                    {copied === s.key ? t('settings.website.urls.copied') : t('settings.website.urls.copy')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* One website, possibly several branches — which one does it speak for? */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground">
                <Globe className="h-4 w-4 text-primary" />
                Your website speaks for
              </h3>
              <p className="mt-1 text-xs text-muted-foreground">
                The chosen branch supplies the website's hours, menu, photos and bookings.
              </p>
            </div>
            <select
              value={siteLocationId ?? ''}
              disabled={!canEdit || savingSite}
              onChange={async (e) => {
                const raw = e.target.value;
                const next = raw === '' ? null : Number(raw);
                const previous = siteLocationId;
                setSiteLocationId(next);          // optimistic — revert on failure
                setSavingSite(true);
                try {
                  setSiteLocationId(
                    await locationsService.setPublicSiteLocation(restaurantId, next)
                  );
                } catch (err) {
                  setSiteLocationId(previous);
                  setError(err?.response?.data?.error || err.message || 'Could not save that choice');
                } finally {
                  setSavingSite(false);
                }
              }}
              className="shrink-0 rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground
                         focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
            >
              <option value="">No branch chosen</option>
              {locations.filter((l) => l.is_active).map((loc) => (
                <option key={loc.id} value={loc.id}>{loc.name}</option>
              ))}
            </select>
          </div>
          {!siteLocationId && (
            <p className="mt-3 text-sm text-warning">
              No branch is chosen, so the website falls back to letting visitors pick one from the
              web address.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Each branch's public address — set on the branch form. */}
      <Card>
        <CardContent className="p-4">
          <h3 className="text-sm font-semibold text-foreground">Branch web addresses</h3>
          <p className="mt-1 mb-3 text-xs text-muted-foreground">
            Published under <span className="font-mono">/site/?outlet=&lt;address&gt;</span>. Set the
            address in Settings → Locations.
          </p>
          <div className="divide-y divide-border">
            {locations.filter((l) => l.is_active).map((loc) => (
              <div key={loc.id} className="flex items-center justify-between gap-3 py-2.5">
                <span className="min-w-0 truncate text-sm text-foreground">{loc.name}</span>
                {loc.public_site_slug ? (
                  <a
                    href={`${publicSites()[0].url}?outlet=${loc.public_site_slug}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex min-w-0 items-center gap-1.5 font-mono text-sm text-primary hover:underline"
                  >
                    <ExternalLink className="h-3.5 w-3.5 shrink-0" />
                    <span className="truncate">{`${publicSites()[0].url}?outlet=${loc.public_site_slug}`}</span>
                  </a>
                ) : (
                  <span className="shrink-0 text-sm text-muted-foreground">Not published</span>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/* ── Page ────────────────────────────────────────────────────────────── */

export default function WebsiteSettings() {
  const { restaurantId } = useRestaurant();
  const { canView, canEdit } = useModuleAccess('settings');
  /* The section IS the address, so a reload lands back on it; the bare path reads as Traffic. */
  const { tab: tabParam } = useParams();
  const tab = SECTIONS.includes(tabParam) ? tabParam : 'traffic';

  if (!canView) return <AccessDenied perm="settings.view" what="website settings" />;

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="font-display text-3xl font-semibold text-foreground tracking-tight leading-none">
          Website Settings
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Your public site: its traffic, photos, menus and bookings.
        </p>
      </div>

      {tab === 'traffic' && <TrafficPanel restaurantId={restaurantId} />}
      {tab === 'photos' && <WebsitePhotos embedded />}
      {tab === 'menus' && <MenusPanel restaurantId={restaurantId} canEdit={canEdit} />}
      {tab === 'reservations' && <ReservationsPanel restaurantId={restaurantId} canEdit={canEdit} />}
      {tab === 'site' && <SitePanel restaurantId={restaurantId} canEdit={canEdit} />}
      {/* Places (Near me): what a guest can type into the public table finder — a website concern, so it lives here. */}
      {tab === 'places' && <FinderPlacesManagement embedded />}
    </div>
  );
}
